import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wallet, TrendingDown, TrendingUp, AlertTriangle, Clock } from 'lucide-react';
import { CashFloat, FloatStatus } from '../../types/agent.types';
import { clsx } from 'clsx';

interface FloatMonitorProps {
  cashFloat: CashFloat | null;
  className?: string;
}

export function FloatMonitor({ cashFloat, className }: FloatMonitorProps) {
  const [showProjection, setShowProjection] = useState(false);
  const navigate = useNavigate();

  if (!cashFloat) {
    return null;
  }

  const utilizationPercent = (cashFloat.currentBalance / cashFloat.maxBalance) * 100;
  const isLow = cashFloat.status === FloatStatus.LOW || cashFloat.status === FloatStatus.CRITICAL;
  const isDepleted = cashFloat.status === FloatStatus.DEPLETED;

  const statusConfig = {
    [FloatStatus.OK]: {
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      label: 'Óptima',
      icon: <TrendingUp className="w-5 h-5" />,
    },
    [FloatStatus.LOW]: {
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-200',
      label: 'Baja',
      icon: <TrendingDown className="w-5 h-5" />,
    },
    [FloatStatus.CRITICAL]: {
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      label: 'Crítica',
      icon: <AlertTriangle className="w-5 h-5" />,
    },
    [FloatStatus.DEPLETED]: {
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      label: 'Agotada',
      icon: <AlertTriangle className="w-5 h-5" />,
    },
  };

  const config = statusConfig[cashFloat.status];

  return (
    <div className={clsx('bg-white rounded-lg border border-gray-200', className)}>
      {/* Header */}
      <div
        className={clsx(
          'px-6 py-4 border-b flex items-center justify-between',
          config.borderColor
        )}
      >
        <div className="flex items-center gap-3">
          <div className={clsx('p-2 rounded-lg', config.bgColor)}>
            <Wallet className={clsx('w-6 h-6', config.color)} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Liquidez en Efectivo</h3>
            <p className="text-sm text-gray-600">
              Estado:{' '}
              <span className={clsx('font-medium', config.color)}>{config.label}</span>
            </p>
          </div>
        </div>
        <div
          className={clsx(
            'flex items-center gap-2 px-3 py-1.5 rounded-full',
            config.bgColor
          )}
        >
          {config.icon}
          <span className={clsx('text-sm font-medium', config.color)}>{config.label}</span>
        </div>
      </div>

      {/* Balance Display */}
      <div className="px-6 py-6">
        <div className="text-center mb-6">
          <p className="text-sm text-gray-600 mb-2">Balance Actual</p>
          <p
            className={clsx(
              'text-4xl font-bold',
              isDepleted ? 'text-red-600' : 'text-gray-900'
            )}
          >
            {cashFloat.currentBalance.toLocaleString()}{' '}
            <span className="text-2xl">{cashFloat.currency}</span>
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Utilización</span>
            <span className={clsx('text-sm font-medium', config.color)}>
              {utilizationPercent.toFixed(1)}%
            </span>
          </div>
          <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={clsx(
                'h-full transition-all duration-500',
                isDepleted ? 'bg-red-500' : isLow ? 'bg-yellow-500' : 'bg-green-500'
              )}
              style={{ width: `${Math.min(utilizationPercent, 100)}%` }}
            />
          </div>
          <div className="flex items-center justify-between mt-2">
            <span className="text-xs text-gray-500">
              Mín: {cashFloat.minBalance.toLocaleString()}
            </span>
            <span className="text-xs text-gray-500">
              Máx: {cashFloat.maxBalance.toLocaleString()}
            </span>
          </div>
        </div>

        {/* Projection */}
        <div
          className={clsx('p-4 rounded-lg border', config.borderColor, config.bgColor)}
        >
          <button
            onClick={() => setShowProjection(!showProjection)}
            className="w-full flex items-center justify-between"
          >
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span className="text-sm font-medium">Proyección de Liquidez</span>
            </div>
            <span className="text-xs text-gray-600">
              {showProjection ? 'Ocultar' : 'Ver'}
            </span>
          </button>

          {showProjection && (
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Promedio diario:</span>
                <span className="font-medium">
                  {Math.round(cashFloat.currentBalance / 30).toLocaleString()} {cashFloat.currency}
                </span>
              </div>
              <div className="mt-3 pt-3 border-t border-gray-300">
                <p className="text-xs italic text-gray-700">
                  💡 Basado en tu actividad actual, tu balance es{' '}
                  {isLow ? 'bajo. Considera solicitar recarga.' : 'adecuado.'}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="grid grid-cols-2 gap-3 mt-6">
          <button
            onClick={() => navigate('/transactions')}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Ver Historial
          </button>
          <button
            onClick={() => navigate('/float/request')}
            className={clsx(
              'px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors',
              isLow || isDepleted ? 'bg-orange-600 hover:bg-orange-700' : 'bg-purple-600 hover:bg-purple-700'
            )}
          >
            Solicitar Recarga
          </button>
        </div>
      </div>
    </div>
  );
}

export default FloatMonitor;
